import React from 'react';
import { useAccessibility } from '../../context/AccessibilityContext';
import { AccessibilityPreferences } from '../../types';

type ColorBlindMode = NonNullable<AccessibilityPreferences['colorBlindMode']>;
type FontScale = NonNullable<AccessibilityPreferences['fontScale']>;
type LineSpacing = NonNullable<AccessibilityPreferences['lineSpacing']>;

const COLOR_BLIND_OPTIONS: { value: ColorBlindMode; label: string; desc: string }[] = [
  { value: 'none',          label: 'None',          desc: 'Standard color vision' },
  { value: 'deuteranopia',  label: 'Deuteranopia',  desc: 'Red-green (green deficient)' },
  { value: 'protanopia',    label: 'Protanopia',    desc: 'Red-green (red deficient)' },
  { value: 'tritanopia',    label: 'Tritanopia',    desc: 'Blue-yellow' },
  { value: 'achromatopsia', label: 'Grayscale',     desc: 'No color perception' },
];

const FONT_SCALE_OPTIONS: { value: FontScale; label: string; px: string }[] = [
  { value: 'normal', label: 'Normal', px: '16px' },
  { value: 'large',  label: 'Large',  px: '18px' },
  { value: 'xl',     label: 'XL',     px: '20px' },
  { value: 'xxl',    label: 'XXL',    px: '22px' },
];

const LINE_SPACING_OPTIONS: { value: LineSpacing; label: string }[] = [
  { value: 'normal',  label: 'Normal' },
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'loose',   label: 'Loose' },
];

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
}

function ToggleRow({ label, description, checked, onChange, id }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <label htmlFor={id} className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
          {label}
        </label>
        <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-muted)' }}>{description}</p>
      </div>
      <button
        id={id}
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
          checked ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
        }`}
      >
        <span className="sr-only">{label}</span>
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export function AccessibilityPanel() {
  const { prefs, update } = useAccessibility();

  return (
    <div className="space-y-6">
      {/* Vision */}
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-text-muted)' }}>
          Vision
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>Color Blind Mode</p>
            <div className="grid grid-cols-1 gap-1.5">
              {COLOR_BLIND_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                    prefs.colorBlindMode === opt.value
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                      : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="colorBlindMode"
                    value={opt.value}
                    checked={prefs.colorBlindMode === opt.value}
                    onChange={() => update({ colorBlindMode: opt.value })}
                    className="accent-indigo-600"
                  />
                  <div>
                    <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{opt.label}</span>
                    <span className="text-xs ml-2" style={{ color: 'var(--theme-text-muted)' }}>{opt.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <ToggleRow
            id="a11y-high-contrast"
            label="High Contrast"
            description="Maximum contrast for text and UI elements"
            checked={!!prefs.highContrast}
            onChange={v => update({ highContrast: v })}
          />
        </div>
      </section>

      {/* Reading */}
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-text-muted)' }}>
          Reading
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>Font Size</p>
            <div className="flex gap-2">
              {FONT_SCALE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => update({ fontScale: opt.value })}
                  className={`flex-1 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    prefs.fontScale === opt.value
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                  }`}
                  style={prefs.fontScale !== opt.value ? { color: 'var(--theme-text)' } : undefined}
                >
                  <span style={{ fontSize: opt.px }}>A</span>
                  <span className="block text-xs mt-0.5">{opt.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs mt-2 italic" style={{ color: 'var(--theme-text-muted)' }}>
              Preview: <span style={{ fontSize: { normal: '16px', large: '18px', xl: '20px', xxl: '22px' }[prefs.fontScale ?? 'normal'] }}>The quick brown fox</span>
            </p>
          </div>

          <ToggleRow
            id="a11y-dyslexic-font"
            label="Dyslexia-Friendly Font"
            description="Uses OpenDyslexic typeface to improve readability"
            checked={!!prefs.dyslexicFont}
            onChange={v => update({ dyslexicFont: v })}
          />

          <div>
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>Line Spacing</p>
            <div className="flex gap-2">
              {LINE_SPACING_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => update({ lineSpacing: opt.value })}
                  className={`flex-1 py-2 rounded-xl border text-sm font-medium transition-colors ${
                    prefs.lineSpacing === opt.value
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                  }`}
                  style={prefs.lineSpacing !== opt.value ? { color: 'var(--theme-text)' } : undefined}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Motion & Focus */}
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-text-muted)' }}>
          Motion &amp; Focus
        </h3>
        <div className="space-y-1 divide-y divide-slate-100 dark:divide-slate-800">
          <ToggleRow
            id="a11y-reduced-motion"
            label="Reduce Motion"
            description="Disables animations and transitions"
            checked={!!prefs.reducedMotion}
            onChange={v => update({ reducedMotion: v })}
          />
          <ToggleRow
            id="a11y-enhanced-focus"
            label="Enhanced Focus Rings"
            description="Prominent outlines on focused elements for keyboard navigation"
            checked={!!prefs.enhancedFocus}
            onChange={v => update({ enhancedFocus: v })}
          />
        </div>
      </section>
    </div>
  );
}
